import styled from 'styled-components';
import { Switch, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import type {
  AirportFilterType,
  AirportsFilterChange,
  SameAirportInboundFilterChange,
  SameAirportOutboundFilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import {
  getFilterInboundSameAirport,
  getFilterOutboundSameAirport,
  mapFilterTypeEventOrigin,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportAirportsFilter';
import { useTransportPageFilterTracking } from '@/app/components/hotel-detail/changeTransport/hooks/useTransportPageFilterTracking';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import useTransportsFetch from '@/app/components/hotel-detail/travel-api/transportsFetch';
import { mapTransportDistance } from '@/app/components/hotel-detail/common/transportDistance';
import { getBoundArrivalCity, getBoundDepartureCity } from '@/app/components/hotel-detail/flight-train/TransportCommon';
import {
  EvoCheckboxRow,
  EvoFilterDivider,
  EvoFilterLoadingLayer,
  EvoFilterSection,
  EvoFilterSectionHeader,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';
import {
  shouldShowSameAirportsToggle,
  toggleIataSelection,
  unionAirportsByIata,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/evoAirportsFilter.utils';

const SameAirportsToggleRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
  padding: 12px 0;
`;

type EvoAirportsFilterProps = {
  inboundArrivalAirports?: AirportFilter[];
  inboundDepartureAirports?: AirportFilter[];
  inboundSameAirport?: SameAirportFilter;
  outboundArrivalAirports?: AirportFilter[];
  outboundDepartureAirports?: AirportFilter[];
  outboundSameAirport?: SameAirportFilter;
  onChange: (
    filterChange: AirportsFilterChange | SameAirportInboundFilterChange | SameAirportOutboundFilterChange
  ) => void;
};

export function EvoAirportsFilter(props: EvoAirportsFilterProps) {
  const {
    inboundArrivalAirports,
    inboundDepartureAirports,
    inboundSameAirport,
    outboundArrivalAirports,
    outboundDepartureAirports,
    outboundSameAirport,
    onChange,
  } = props;

  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  const { data: alternativeTransportsData } = useTransportsFetch();
  const transportDistance = (alternativeTransportsData as FullAvailableTransportResponse)?.distances;
  const distancesData = mapTransportDistance(transportDistance);

  const { data: selectedTransportData } = useSelectedTransport();
  const outboundData = selectedTransportData?.transports[0]?.outbound;
  const originCityName = outboundData ? getBoundDepartureCity(outboundData) : undefined;
  const destinationCityName = outboundData ? getBoundArrivalCity(outboundData) : undefined;

  const showSameAirportsToggle = shouldShowSameAirportsToggle(outboundDepartureAirports, inboundDepartureAirports);
  const originAirports = unionAirportsByIata(outboundDepartureAirports, inboundArrivalAirports);
  const destinationAirports = unionAirportsByIata(outboundArrivalAirports, inboundDepartureAirports);

  if (!showSameAirportsToggle && originAirports.length === 0 && destinationAirports.length === 0) return null;

  const onSameAirportsToggle = (enabled: boolean) => {
    const outboundChange = getFilterOutboundSameAirport(
      enabled,
      sendCuriosityClickEvent,
      sendStratrackFlightPageFilterEvent
    );
    const inboundChange = getFilterInboundSameAirport(
      enabled,
      sendCuriosityClickEvent,
      sendStratrackFlightPageFilterEvent
    );
    onChange({
      newState: { ...outboundChange.newState, ...inboundChange.newState },
      event: outboundChange.event,
    });
  };

  const sendAirportTrackingEvents = (type: AirportFilterType, list: AirportFilter[] | undefined, iata: string) => {
    const airport = list?.find((listAirport) => listAirport.iata === iata);
    if (!airport) return;
    const origin = mapFilterTypeEventOrigin[type];
    sendStratrackFlightPageFilterEvent({ filterName: origin, value: iata, deselectFlag: airport.selected });
    sendCuriosityClickEvent({
      event_name: `click_on_${origin}_airport_filters`,
      event_context: 'change_transport_page',
      event_value: iata,
    });
  };

  const onOriginAirportToggle = (iata: string) => {
    const { primary, secondary } = toggleIataSelection(iata, outboundDepartureAirports, inboundArrivalAirports);
    sendAirportTrackingEvents('outboundDepartureAirports', outboundDepartureAirports, iata);
    sendAirportTrackingEvents('inboundArrivalAirports', inboundArrivalAirports, iata);
    onChange({
      newState: {
        ...(primary && { outboundDepartureAirports: primary }),
        ...(secondary && { inboundArrivalAirports: secondary }),
      } as AirportsFilterChange['newState'],
      event: { type: 'FILTER', origin: 'DEPARTURE_AIRPORTS_OUTBOUND_FILTER' },
    });
  };

  const onDestinationAirportToggle = (iata: string) => {
    const { primary, secondary } = toggleIataSelection(iata, outboundArrivalAirports, inboundDepartureAirports);
    sendAirportTrackingEvents('outboundArrivalAirports', outboundArrivalAirports, iata);
    sendAirportTrackingEvents('inboundDepartureAirports', inboundDepartureAirports, iata);
    onChange({
      newState: {
        ...(primary && { outboundArrivalAirports: primary }),
        ...(secondary && { inboundDepartureAirports: secondary }),
      } as AirportsFilterChange['newState'],
      event: { type: 'FILTER', origin: 'ARRIVAL_AIRPORTS_OUTBOUND_FILTER' },
    });
  };

  const getDistanceSubLabel = (iata: string): string | undefined => {
    const distance = distancesData.find((distanceData) => distanceData.code === iata)?.distanceWithUnit;
    return distance
      ? `${distance.value} ${distance.unit} ${t('vacation.flightavailability.filter.airportdistance')}`
      : undefined;
  };

  const sameAirportsLabel = isTrain
    ? t('vacation.oss.change_transport.filters.same_stations')
    : t('vacation.oss.change_transport.filters.same_airports');

  const originTitle =
    originCityName ??
    (isTrain
      ? t('vacation.common.filters.station.departure.title')
      : t('vacation.common.filters.airport.departure.title'));
  const destinationTitle =
    destinationCityName ??
    (isTrain ? t('vacation.common.filters.station.arrival.title') : t('vacation.common.filters.airport.arrival.title'));

  return (
    <>
      {showSameAirportsToggle && (
        <>
          <SameAirportsToggleRow>
            {/* MPH-398: Figma toggle label is Body 16/24 #1e0023 — use Text, not a bare span */}
            <Text variant="Body" color="textPrimary">
              {sameAirportsLabel}
            </Text>
            <Switch
              label=""
              isLabelVisible={false}
              checked={Boolean(outboundSameAirport?.on || inboundSameAirport?.on)}
              onChange={onSameAirportsToggle}
            />
            <EvoFilterLoadingLayer origins={['OUTBOUND_SAME_AIRPORT_FILTER', 'INBOUND_SAME_AIRPORT_FILTER']} />
          </SameAirportsToggleRow>
          <EvoFilterDivider />
        </>
      )}
      {originAirports.length > 0 && (
        <EvoFilterSection>
          <EvoFilterSectionHeader title={originTitle} />
          {originAirports.map((airport) => (
            <EvoCheckboxRow
              key={airport.iata}
              id={`evo-origin-airport-${airport.iata}`}
              label={`${airport.name} (${airport.iata})`}
              checked={airport.selected}
              disabled={!airport.enabled}
              onChange={() => onOriginAirportToggle(airport.iata)}
            />
          ))}
          <EvoFilterLoadingLayer origins={['DEPARTURE_AIRPORTS_OUTBOUND_FILTER']} />
        </EvoFilterSection>
      )}
      {originAirports.length > 0 && destinationAirports.length > 0 && <EvoFilterDivider />}
      {destinationAirports.length > 0 && (
        <EvoFilterSection>
          <EvoFilterSectionHeader title={destinationTitle} />
          {destinationAirports.map((airport) => (
            <EvoCheckboxRow
              key={airport.iata}
              id={`evo-destination-airport-${airport.iata}`}
              label={`${airport.name} (${airport.iata})`}
              subLabel={getDistanceSubLabel(airport.iata)}
              checked={airport.selected}
              disabled={!airport.enabled}
              onChange={() => onDestinationAirportToggle(airport.iata)}
            />
          ))}
          <EvoFilterLoadingLayer origins={['ARRIVAL_AIRPORTS_OUTBOUND_FILTER']} />
        </EvoFilterSection>
      )}
    </>
  );
}
